import { computed, reactive } from '@vue/composition-api';
import { $t } from '@/i18n';

const legacyStore = (iframe = null) => {
  let app = window.parent?.app;
  if (iframe) {
    const legacyTKOIframe =
      iframe.contentWindow.document.getElementsByClassName('tw-legacyViewWrapper');
    if (legacyTKOIframe.length) app = legacyTKOIframe[0].contentWindow?.app;
  }

  return app?.hybrid?.store;
};

function openTkoModal(name, opts = {}) {
  window.parent?.app?.modal?.Show(name, opts);
}

function openHybridModal(name, props = {}, { iframe = null } = {}) {
  legacyStore(iframe).dispatch('modals/open', {
    name,
    props,
  });
}

function openNextModalInLegacyApp(name, props = {}) {
  legacyStore().dispatch('nextModals/open', {
    name,
    props,
  });
}

const modalState = reactive({
  modals: [],
});

export default function useModalManager({ legacyConnector, routeManager }) {
  const activeModal = computed(() => modalState.modals[modalState.modals.length - 1]);
  const isModalRoute = computed(() =>
    routeManager.currentRoute.value.fullPath.startsWith('/modals/'),
  );

  function addModal(name = '', props = {}) {
    if (!name || activeModal.value?.name === name) {
      return;
    }

    const modal = {
      id: `modal-${modalState.modals.length}`,
      name,
      props,
    };

    modalState.modals.push(modal);
  }

  function removeModal(modalIndex) {
    if (modalIndex) {
      modalState.modals.splice(modalIndex, 1);
    } else {
      modalState.modals.splice(modalState.modals.length - 1, 1);
    }
  }

  function openModal(name, props = {}) {
    // The local modal state can be used in 2 scenarios:
    // - We are rendering the next app on its own
    // - We are rendering a modal via the route in hybrid and we want to open a subsequent modal
    if (
      !legacyConnector.isLegacyConnected.value ||
      (legacyConnector.isLegacyConnected.value && isModalRoute.value)
    ) {
      addModal(name, props);
    } else {
      openNextModalInLegacyApp(name, props);
    }
  }

  function closeModal(index = undefined) {
    // Only try to close modals if there are any to close (could be a route modal)
    if (modalState.modals.length > 0) {
      removeModal(index);
    } else if (legacyConnector.isLegacyConnected.value) {
      legacyStore().dispatch('nextModals/close');
    }
  }

  async function openDialog({
    heading = new Error('Dialog: Missing heading'),
    message = new Error('Dialog: Missing message'),
    type = 'information',
    ...props
  } = {}) {
    return new Promise((resolve) => {
      openModal('ConfirmDialog', {
        heading,
        message,
        type,
        confirmCallback: () => resolve(true),
        rejectCallback: () => resolve(false),
        ...props,
      });
    });
  }

  // We dont pass in the closeModal here because we might want to do something in the modal
  const interceptClose = ({
    v$,
    closeModal: _closeModal = closeModal,
    heading = $t('You have changes'),
    message = $t('Are you sure you want to discard these changes?'),
    confirmButtonText = $t('OK'),
  } = {}) => {
    return async () => {
      if (v$?.value?.$anyDirty) {
        const confirmed = await openDialog({
          heading,
          message,
          confirmButtonText,
        });
        if (confirmed) {
          _closeModal();
        }
      } else {
        _closeModal();
      }
    };
  };

  return {
    activeModal,
    closeModal,
    isModalRoute,
    interceptClose,
    modalState,
    openDialog,
    openHybridModal,
    openModal,
    openTkoModal,
  };
}
